/* eslint react-hooks/exhaustive-deps: "warn" */
import { useContext } from 'react';

import { ContentContext } from 'context/ContentContext';

import { useInt } from 'hooks/useInt';

import { Button } from 'ui/atoms/Button/Button';
import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';

import styles from './SalesProspectingLeadGenerationAutomation.module.scss';

type Translate = (key: string) => string;

type Action = {
  label: string;
  route?: string;
  href?: string;
};

type Row = {
  num: string;
  title: string;
  description: string;
  actions: Action[];
};

const getRows = (t: Translate, bookDemoSlug: string): Row[] => [
  {
    num: '01',
    title: t('Platform'),
    description: t('Build a segment in the app and export the rows. No API, no engineering ticket.'),
    actions: [{ label: t('Start free trial'), route: '/free-trial' }],
  },
  {
    num: '02',
    title: 'API',
    description: t('Search, match and enrich in one call, straight inside your own tooling.'),
    actions: [{ label: t('Read the docs'), href: 'https://api.globaldatabase.com/docs/v2/#setup' }],
  },
  {
    num: '03',
    title: t('Bulk file'),
    description: t('Send your account master as a file, get registry fields back beside your own columns.'),
    actions: [{ label: t('Talk to sales'), route: bookDemoSlug }],
  },
  {
    num: '04',
    title: 'CRM',
    description: t('Records land on the accounts you already own, deduplicated on registry identifier.'),
    actions: [
      { label: t('Add to Salesforce'), href: 'https://appexchange.salesforce.com/appxListingDetail?listingId=7d8cb601-add2-47f7-a345-4008cbb0cfe4' },
      { label: t('Add to HubSpot'), href: 'https://ecosystem.hubspot.com/marketplace/listing/global-database-intelligence-enrichment' },
    ],
  },
  {
    num: '05',
    title: t('Browser extension'),
    description: t('Resolve the legal entity behind any website or profile, then push it to the CRM.'),
    actions: [{ label: t('Add to Chrome'), href: 'https://chromewebstore.google.com/detail/global-database-extension/bjahjijchjnokbgbahdmfcjoblkicank' }],
  },
  {
    num: '06',
    title: 'Claude & ChatGPT',
    description: t('Ask for a segment in plain language and get the rows back with their sources attached.'),
    actions: [
      { label: t('Add to Claude'), href: 'https://claude.ai/new#directory/connectors/global-database' },
      { label: t('Add to ChatGPT'), href: 'https://chatgpt.com/plugins/plugin_asdk_app_696f807d21a481918a1ed1f43d719ce9' },
    ],
  },
  {
    num: '07',
    title: 'MCP server',
    description: t('Segment search and entity resolution exposed as tools to any MCP client, on the same key.'),
    actions: [{ label: t('View setup'), href: '/mcp' }],
  },
  {
    num: '08',
    title: 'Regis AI',
    description: t('Describe a market and it plans which registers can answer, then returns every row sourced.'),
    actions: [{ label: t('See Regis'), href: '/regis' }],
  },
];

const ArrowIcon = () => (
  <svg width="13" height="13" viewBox="0 0 15 15" fill="none">
    <path
      d="M3 7.5h9M8.5 3.5l4 4-4 4"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const SalesProspectingLeadGenerationAutomation = () => {
  const { t } = useInt();
  const { secondaryNavigation } = useContext(ContentContext);
  const rows = getRows(t, secondaryNavigation.book_demo?.slug || 'book-a-demo');

  return (
    <section className={styles.section}>
      <div className={styles.container}>
        <div className={styles.marker}>
          <span className={styles.markerNum}>08</span> / {t('Automation')}
        </div>

        <div className={styles.body}>
          <div className={styles.head}>
            <TitleBlock
              align="left"
              title={t('The list is the start, not the deliverable')}
              description={t(
                'Prospecting data that lives in a CSV decays from the moment it is downloaded. Ours is wired into the systems that run the motion, and it keeps correcting itself after delivery.',
              )}
            />
          </div>

          <div className={styles.lower}>
            <div className={styles.railNote}>
              {t('Eight ways in. One corpus, one key, the same provenance on every field.')}
            </div>

            <div className={styles.rows}>
              {rows.map((row) => (
                <div key={row.title} className={styles.row}>
                  <span className={styles.num}>{row.num}</span>

                  <div className={styles.rowHead}>
                    <h3>{row.title}</h3>
                  </div>

                  <div className={styles.rowBody}>
                    <p>{row.description}</p>

                    <div className={styles.rowActions}>
                      {row.actions.map((action) => (
                        <Button
                          key={action.label}
                          type="transparent"
                          size="md"
                          {...(action.href ? { href: action.href } : { route: action.route as string })}
                          suffix={<ArrowIcon />}
                        >
                          {action.label}
                        </Button>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
